import type { FunctionComponent } from 'react'
import React from 'react'
import { ButtonLink, TypographyV2 as Typography } from '@which/seatbelt'
import {
  PadlockIcon,
  PadlockOpenIcon,
  SolidPadlockIcon,
} from '@which/seatbelt/src/components/Icons/Miscellaneous'

import classnames from 'classnames'

import { useUserTraitsQuery } from '../../../generated/frontend'
import { useSubscriptionCostInfo } from '../../hooks/useSubscriptionCostInfo'
import { usePageProps } from '../../usePageProps'
import { Link } from '../Link'
import styles from './SinglePadlockCta.module.scss'

export const SinglePadlockCta: FunctionComponent<Props> = ({
  buttonCopy = 'Unlock table',
  displayButtonIcon = true,
  displayStandfirst = true,
  horizontal = false,
  lockedPadlockIcon = true,
  standfirst = 'Get instant access to this and all our scores and recommendations',
  title = 'Sign up to reveal',
  overlineText = '',
  type,
}) => {
  const subscriptionCostInfo = useSubscriptionCostInfo()

  let updatedButtonCopy = buttonCopy
  let updatedOverlineText = overlineText
  let updatedTitle = title
  const linkURL = 'https://join.which.co.uk/join/subscribe'
  const smallPrint = subscriptionCostInfo.CTACaptionText

  const { userAccessState } = usePageProps()
  const transformTypeDecision = userAccessState?.transformTypeDecision ?? ''
  const isLapsedMemberByPaywallStatus = transformTypeDecision === 'AUTHENTICATED_NO_ACCESS'

  const { data: { userTraits } = {} } = useUserTraitsQuery()
  const isLapsedMemberByPercyStatus = userTraits?.hasLapsedSubscription ?? false

  const isLapsedMember = isLapsedMemberByPaywallStatus || isLapsedMemberByPercyStatus

  if (isLapsedMember) {
    updatedOverlineText = 'Welcome back'
    updatedTitle = 'Pick up where you left off'
    updatedButtonCopy = 'Resubscribe'
  }

  // Use this when the lapsed offer is back - https://whichonline.atlassian.net/browse/WHC-1064
  // const isLapsedMemberOfferEnabled = useFeatureIsOn('whc-1064-lapsed-member-offer')
  // const showLapsedMemberOffer = isLapsedMemberOfferEnabled && (isLapsedMemberByPaywallStatus || isLapsedMemberByPercyStatus)
  // if (showLapsedMemberOffer) {
  //   // Update required props here
  // }

  return (
    <div
      className={classnames(styles.ctaWrapper, type === 'desktop' && styles['ctaWrapperDesktop'], {
        [styles.horizontal]: horizontal,
      })}
    >
      <div className={styles.icon}>{lockedPadlockIcon ? <PadlockIcon /> : <PadlockOpenIcon />}</div>
      <div className={styles.ctaContent}>
        {updatedOverlineText && (
          <Typography textStyle="sb-text-heading-overline" className={styles.overline}>
            {updatedOverlineText}
          </Typography>
        )}
        <Typography textStyle="sb-text-heading-small">{updatedTitle}</Typography>
        {displayStandfirst && (
          <Typography textStyle="sb-text-body-small-regular" className={styles.contentWrapper}>
            {standfirst}
          </Typography>
        )}
        <ButtonLink
          appearance="secondary"
          href={linkURL}
          className={styles.cta}
          data-which-id="nstunlockedprovider-signup-cta"
        >
          {displayButtonIcon && <SolidPadlockIcon width={15} height={15} viewBox="0 0 15 15" />}
          {updatedButtonCopy}
        </ButtonLink>

        <Typography textStyle="sb-text-body-x-small-regular">{smallPrint}</Typography>

        {!isLapsedMemberByPaywallStatus && (
          <Typography textStyle="sb-text-body-default-regular">
            <span>Already a member? </span>
            <Link href="/login">Log in</Link>
          </Typography>
        )}
      </div>
    </div>
  )
}

type Props = {
  buttonCopy?: string
  displayButtonIcon?: boolean
  displayStandfirst?: boolean
  horizontal?: boolean
  lockedPadlockIcon?: boolean
  standfirst?: string
  title?: string
  overlineText?: string
  type?: string
}
